/**
 * Sürüm İzleyici — "Ctrl+F5" yerine kendiliğinden güncelleme
 * =========================================================
 * Yeni bir frontend build yayınlandığında kullanıcı eski bundle ile kalır.
 * Eski çözüm: 5 sn sayım → localStorage'ı (TOKEN DAHİL) sil → /login'e hard
 * reload. Bu üç şeyi birden bozuyordu:
 *   · kullanıcı ORTASINDA olduğu işi kaybediyordu (yarım fiş, dolu form)
 *   · her güncellemede herkes login'e düşüyordu — oysa oturumun bozulması
 *     için hiçbir sebep yok, yeni bundle'ı almak için reload yeter
 *   · müşteri linkleri (iade/takip) formu değil login ekranını açıyordu
 *
 * YENİ DAVRANIŞ
 *   1) Uyuşmazlık görülünce ÜSTTE İNCE BİR ŞERİT çıkar: "Yeni sürüm hazır".
 *      Hiçbir şey kesilmez, kullanıcı işine devam eder.
 *   2) Kullanıcı isterse "Şimdi güncelle"ye basar.
 *   3) Basmazsa: SAYFA DEĞİŞTİRİRKEN kendiliğinden uygulanır — çünkü o an
 *      ekranda yarım kalmış bir iş yoktur. Bu "fişi kaydedince alır" demektir.
 *   4) Güvenli değilse (dolu form, açık modal, uçuşta istek) ERTELENİR.
 *   5) Oturum KORUNUR: localStorage silinmez, sadece sayfa yenilenir.
 *
 * Tetikleyiciler: axios response header'ı, sekmeye dönüş, focus, 60 sn timer.
 *
 * ⚠️ DEV MODE: NODE_ENV='development' ise kontrol KAPALI. Dev'de her yarn start
 *    version.json'u yeniden üretir → sonsuz uyarı döngüsü olurdu.
 */
import axios from 'axios';
import versionJson from '../version.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CURRENT_VERSION = versionJson.version;
export const CURRENT_BUILD_TIME = versionJson.build_time;

const IS_DEV = process.env.NODE_ENV !== 'production';
const DEV_VERSION = 'dev';
const STORAGE_KEY_SEEN_VERSION = 'yetsan_app_version';
const STORAGE_KEY_RELOAD_GUARD = 'yetsan_version_reload_guard';

let _isReloading = false;
let _intervalTimer = null;
let _acikIstek = 0;   // uçuşta axios isteği — kayıt sürerken yenileme yapılmasın

// Bekleyen güncelleme: {version} | null. Şerit ve otomatik uygulama bunu okur.
let _bekleyen = null;
const _aboneler = new Set();

/** Şeridin/uygulamanın durumu dinlemesi için. Abonelikten çıkış fonksiyonu döner. */
export function guncellemeyiDinle(fn) {
  _aboneler.add(fn);
  fn(_bekleyen);
  return () => _aboneler.delete(fn);
}

export function bekleyenGuncelleme() {
  return _bekleyen;
}

function yayinla() {
  _aboneler.forEach((f) => { try { f(_bekleyen); } catch (_) { /* abone hatası yayını durdurmasın */ } });
}

function isVersionMismatch(serverVersion) {
  // Dev mode'da ASLA mismatch çıkarma
  if (IS_DEV) return false;
  if (!serverVersion) return false;
  if (CURRENT_VERSION === DEV_VERSION || serverVersion === DEV_VERSION) return false;
  return String(serverVersion) !== String(CURRENT_VERSION);
}

function setReloadGuard(serverVersion) {
  try {
    sessionStorage.setItem(STORAGE_KEY_RELOAD_GUARD, String(serverVersion));
  } catch (_) { /* ignore */ }
}

/**
 * Kullanıcı son sayfa değişiminden bu yana bir şey YAZDI mı?
 *
 * ⚠️ İlk deneme `input.value !== input.defaultValue` idi ve bu uygulamada
 * ÇALIŞMIYOR: React kontrollü inputlarda `value` ATTRIBUTE'u da güncelleniyor,
 * yani defaultValue hep value'ya eşit çıkıyor ve dolu bir form "boş" görünüyor
 * (tarayıcı testinde yakalandı). Bu yüzden DOM'un haline değil, kullanıcının
 * EYLEMİNE bakılıyor: gerçek bir input/change olayı olduysa yazılmış demektir.
 * React'in kendi render'ı bu olayları üretmez, yalnız kullanıcı üretir.
 */
let _yazildi = false;

export function yazmaIzniniSifirla() {
  _yazildi = false;
}

function yazmaDinleyicisiKur() {
  if (typeof document === 'undefined') return;
  const isaretle = (e) => {
    // Güvenilen olay: gerçekten kullanıcıdan geldiyse (isTrusted)
    if (e && e.isTrusted === false) return;
    const h = e && e.target;
    if (!h || !h.tagName) return;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(h.tagName)) _yazildi = true;
  };
  document.addEventListener('input', isaretle, true);
  document.addEventListener('change', isaretle, true);
}

/**
 * Şu an yenilemek GÜVENLİ mi? Amaç: kaydedilmemiş hiçbir şeyi kaybetmemek.
 *
 * Üç engel:
 *   · kullanıcı bu sayfada bir şey yazdı → yarım iş olabilir
 *   · açık modal/diyalog → içinde yarım bir işlem olabilir
 *   · uçuşta istek → kayıt işlemi sürüyor olabilir
 *
 * Sayfa DEĞİŞİMİNDE yazma izi sıfırlanır: kullanıcı fişini kaydedip başka
 * ekrana geçtiyse kaybolacak bir şey kalmamıştır.
 */
export function yenilemeGuvenliMi() {
  if (typeof document === 'undefined') return false;
  if (_acikIstek > 0) return false;
  if (_yazildi) return false;
  try {
    if (document.querySelector('[role="dialog"], dialog[open], .modal-acik')) return false;
  } catch (_) {
    return false;   // DOM okunamadıysa riske girme
  }
  return true;
}

/** Uyuşmazlık görüldü → ŞERİDİ göster. Hiçbir şey kesilmez. */
function guncellemeBildir(serverVersion) {
  if (_isReloading) return;
  if (_bekleyen && _bekleyen.version === String(serverVersion)) return;
  _bekleyen = { version: String(serverVersion), tarih: Date.now() };
  // eslint-disable-next-line no-console
  console.info(`[AppVersion] yeni sürüm hazır — current=${CURRENT_VERSION} server=${serverVersion}`);
  yayinla();
}

/**
 * Güncellemeyi uygula: SADECE sayfayı yenile.
 *
 * localStorage SİLİNMEZ — oturumu kapatmanın hiçbir gerekçesi yok; yeni
 * bundle zaten yeni index.html'den hash'li dosya adlarıyla iniyor. Eskiden
 * token da silindiği için her güncelleme = herkes login'e düşüyordu.
 */
export function guncellemeyiUygula() {
  if (_isReloading || !_bekleyen) return;
  _isReloading = true;
  setReloadGuard(_bekleyen.version);
  try { localStorage.setItem(STORAGE_KEY_SEEN_VERSION, _bekleyen.version); } catch (_) { /* ignore */ }
  const u = new URL(window.location.href);
  u.searchParams.set('v', _bekleyen.version);   // önbellek kırıcı
  window.location.replace(u.toString());
}

/**
 * Sayfa değişiminde çağrılır: güvenliyse güncellemeyi uygula.
 * Sayfa değişimi doğal bir "ara": o an ekranda yarım kalmış bir iş yoktur,
 * kullanıcı fişini kaydedip başka ekrana geçmiştir.
 */
export function guvenliAndaUygula() {
  if (!_bekleyen || _isReloading) return false;
  // 🛡️ DÖNGÜ KORUMASI: bu sürüm için zaten bir kez yenilendiysek ve hâlâ
  // uyuşmuyorsa, sunucu tarafında bir sorun var demektir (ör. version.txt
  // güncellenmemiş). Tekrar tekrar yenilemek kullanıcıyı sonsuz döngüye
  // sokar. Şerit görünür kalır, otomatik uygulama durur.
  if (ayniSurumIcinDenendi(_bekleyen.version)) {
    // eslint-disable-next-line no-console
    console.warn('[AppVersion] bu sürüm için zaten yenilendi — otomatik uygulama durduruldu');
    return false;
  }
  if (!yenilemeGuvenliMi()) return false;
  guncellemeyiUygula();
  return true;
}

/** Bu sürüm için bu oturumda zaten bir yenileme denendi mi? */
function ayniSurumIcinDenendi(v) {
  try {
    return sessionStorage.getItem(STORAGE_KEY_RELOAD_GUARD) === String(v);
  } catch (_) {
    return false;
  }
}

/** Axios interceptor — her response header'ında X-App-Version kontrolü. */
export function installVersionAxiosInterceptor() {
  if (IS_DEV) return;  // Dev'de hiç interceptor takma
  // Uçuşta istek sayımı: bir kayıt sürerken sayfa yenilenirse işlem yarıda kalır
  axios.interceptors.request.use((cfg) => { _acikIstek += 1; return cfg; },
    (err) => { _acikIstek = Math.max(0, _acikIstek - 1); return Promise.reject(err); });
  axios.interceptors.response.use(
    (response) => {
      _acikIstek = Math.max(0, _acikIstek - 1);
      try {
        const serverVersion = response?.headers?.['x-app-version'] || response?.headers?.['X-App-Version'];
        if (isVersionMismatch(serverVersion)) guncellemeBildir(serverVersion);
      } catch (_) { /* atla */ }
      return response;
    },
    (error) => {
      _acikIstek = Math.max(0, _acikIstek - 1);
      try {
        const serverVersion = error?.response?.headers?.['x-app-version'];
        if (isVersionMismatch(serverVersion)) guncellemeBildir(serverVersion);
      } catch (_) { /* atla */ }
      return Promise.reject(error);
    }
  );
}

async function fetchAndCheckServerVersion() {
  if (IS_DEV) return;
  try {
    const res = await axios.get(`${API}/version`, { timeout: 8000 });
    const v = res?.data?.version;
    if (isVersionMismatch(v)) guncellemeBildir(v);
  } catch (_) { /* network hatası: atla */ }
}

export function startVersionWatcher() {
  if (typeof window === 'undefined') return;
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.info('[AppVersion] dev mode — version kontrolü devre dışı');
    return;
  }

  yazmaDinleyicisiKur();

  // Arka plandaki sekme sormaz (öne gelince zaten hemen sorar); sekmeye dönüşte focus + visibilitychange
  // aynı anda geldiği için 5 sn içindeki ikinci çağrı atlanır (2026-10-23)
  let sonKontrol = 0;
  const kontrol = () => {
    if (Date.now() - sonKontrol < 5000) return;
    sonKontrol = Date.now();
    fetchAndCheckServerVersion();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') kontrol();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', kontrol);

  if (_intervalTimer) clearInterval(_intervalTimer);
  _intervalTimer = setInterval(() => { if (!document.hidden) kontrol(); }, 60000);

  fetchAndCheckServerVersion();
}

export function stopVersionWatcher() {
  if (_intervalTimer) clearInterval(_intervalTimer);
  _intervalTimer = null;
}
